import React, { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import * as planService from "../services/planService";
import { PlanMeta } from "../services/planService";

function TabConfig() {
  const [plans, setPlans] = useState<PlanMeta[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [newPlanTitle, setNewPlanTitle] = useState("");
  const [mode, setMode] = useState<"select" | "create">("select");
  const [contextId, setContextId] = useState("");
  const [contextInfo, setContextInfo] = useState<any>({});

  useEffect(() => {
    microsoftTeams.app.initialize().then(async () => {
      const ctx = await microsoftTeams.app.getContext();

      const isInChannel = !!(ctx.team?.groupId && ctx.channel?.id);
      const isInChat = !!(ctx.chat?.id) && !isInChannel;

      let ctxId = "";
      const info: any = {};

      if (isInChannel) {
        ctxId = `channel-${ctx.team!.groupId}-${ctx.channel!.id}`.replace(/[^a-zA-Z0-9-]/g, "-");
        info.contextType = "channel";
        info.teamId = ctx.team!.groupId;
        info.channelId = ctx.channel!.id;
      } else if (isInChat) {
        ctxId = `chat-${ctx.chat!.id}`.replace(/[^a-zA-Z0-9-]/g, "-");
        info.contextType = "chat";
        info.chatId = ctx.chat!.id;
      } else {
        // Use loginHint (email) for personal context to match across Teams and web
        const email = (ctx.user as any)?.loginHint || (ctx.user as any)?.userPrincipalName || ctx.user?.id || "";
        ctxId = `personal-${email}`.replace(/[^a-zA-Z0-9-_.@]/g, "-");
        info.contextType = "personal";
      }

      setContextId(ctxId);
      setContextInfo(info);

      // Load existing plans for this context
      try {
        const existing = await planService.listPlans(ctxId);
        setPlans(existing);
        if (existing.length > 0) {
          setSelectedPlanId(existing[0].id);
        } else {
          setMode("create");
        }
      } catch {
        setMode("create");
      }

      // Register save handler
      microsoftTeams.pages.config.registerOnSaveHandler(async (saveEvent) => {
        try {
          let planId = selectedPlanId;

          // If creating a new plan
          if (mode === "create" && newPlanTitle.trim()) {
            const plan = await planService.createPlan({
              title: newPlanTitle.trim(),
              contextId: ctxId,
              ...info,
            });
            planId = plan.id;
          }

          const baseUrl = `https://${window.location.hostname}:${window.location.port}`;
          microsoftTeams.pages.config.setConfig({
            suggestedDisplayName: "Gantt Project Planner",
            entityId: planId || "gantt-tab",
            contentUrl: `${baseUrl}/index.html#/tab`,
            websiteUrl: `${baseUrl}/index.html#/tab`,
          });
          saveEvent.notifySuccess();
        } catch (err) {
          saveEvent.notifyFailure("Failed to save configuration");
        }
      });

      microsoftTeams.pages.config.setValidityState(true);
    });
  }, []);

  // Update validity when form changes
  useEffect(() => {
    const isValid = mode === "select" ? !!selectedPlanId : !!newPlanTitle.trim();
    try {
      microsoftTeams.pages.config.setValidityState(isValid);
    } catch {}
  }, [mode, selectedPlanId, newPlanTitle]);

  return (
    <div style={{ padding: 24, maxWidth: 480, fontFamily: "Segoe UI, sans-serif" }}>
      <h2 style={{ margin: "0 0 16px" }}>Gantt Project Planner</h2>

      {plans.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => setMode("select")}
              style={{
                padding: "8px 16px", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer",
                background: mode === "select" ? "#9f27b3" : "#fff",
                color: mode === "select" ? "#fff" : "#333",
              }}
            >
              Use existing plan
            </button>
            <button
              onClick={() => setMode("create")}
              style={{
                padding: "8px 16px", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer",
                background: mode === "create" ? "#9f27b3" : "#fff",
                color: mode === "create" ? "#fff" : "#333",
              }}
            >
              Create new plan
            </button>
          </div>
        </div>
      )}

      {mode === "select" && plans.length > 0 && (
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
            Select a plan:
          </label>
          <select
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
          >
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === "create" && (
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
            Plan name:
          </label>
          <input
            type="text"
            value={newPlanTitle}
            onChange={(e) => setNewPlanTitle(e.target.value)}
            placeholder="My Project Plan"
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
            autoFocus
          />
        </div>
      )}

      <p style={{ marginTop: 16, fontSize: 13, color: "#888" }}>
        Click Save to add the Gantt chart to this tab.
      </p>
    </div>
  );
}

export default TabConfig;
